import React from "react"
import { BsLightningCharge } from "react-icons/bs"
import { IoLink } from "react-icons/io5"
import { MdPhotoLibrary } from "react-icons/md"

import { EnabledPlugin } from "@fuse/recoil/plugins"

/**
 * Check if a plugin is connected to an event
 * @param pluginId - The ID of the plugin to check
 * @param enabledPlugins - Array of enabled plugins from event data
 * @returns true if the plugin is connected to the event
 */
export const isPluginConnectedToEvent = (
  pluginId: string,
  enabledPlugins: EnabledPlugin[] = [],
): boolean => {
  return enabledPlugins.some((plugin) => plugin.id === pluginId)
}

/**
 * Get the appropriate icon type based on plugin connection status
 * @param isConnected - Whether the plugin is connected
 * @param isInstant - Whether this is an instant plugin
 * @returns Icon type string
 */
export const getPluginConnectionIconType = (
  isConnected: boolean,
  isInstant: boolean = false,
): string => {
  if (isConnected) {
    return "edit" // Edit/configure icon for connected plugins
  }

  if (isInstant) {
    return "lightning" // Lightning bolt for instant plugins
  }

  return "plug" // Plug icon for regular plugins that aren't connected
}

/**
 * Get the main plugin icon type
 * @param isInstant - Whether this is an instant plugin
 * @returns Icon type string
 */
export const getMainPluginIconType = (isInstant: boolean = false): string => {
  if (isInstant) {
    return "lightning" // Lightning bolt for instant plugins
  }

  return "puzzle" // Puzzle piece for regular plugins
}

/**
 * Get icon props for a connection icon
 */
export const getConnectionIconProps = (
  isConnected: boolean,
  isInstant: boolean = false,
) => {
  if (isConnected) {
    // Edit icon for connected plugins
    return {
      viewBox: "0 0 17 15",
      fill: "none",
      className: "text-blue-400",
      fillPaths: [
        {
          d: "M13.1719 3.86719L11.5547 2.25781L12.4453 1.38281C12.8828 0.953125 13.375 0.914062 13.7812 1.32031L14.1094 1.65625C14.5156 2.0625 14.4922 2.54688 14.0547 2.98438L13.1719 3.86719ZM3.39844 13.6172L1.44531 14.3672C1.19531 14.4609 0.921875 14.1875 1.02344 13.9219L1.79688 12.0156L10.7734 3.03906L12.375 4.64844L3.39844 13.6172ZM4.34375 14.3594L6.09375 12.6172H16.1328C16.6094 12.6172 17 13.0078 17 13.4844C17 13.9609 16.6094 14.3594 16.1328 14.3594H4.34375Z",
          fill: "#7F7F7F",
          fillOpacity: 0.5,
          style: { mixBlendMode: "luminosity" },
        },
        {
          d: "M13.1719 3.86719L11.5547 2.25781L12.4453 1.38281C12.8828 0.953125 13.375 0.914062 13.7812 1.32031L14.1094 1.65625C14.5156 2.0625 14.4922 2.54688 14.0547 2.98438L13.1719 3.86719ZM3.39844 13.6172L1.44531 14.3672C1.19531 14.4609 0.921875 14.1875 1.02344 13.9219L1.79688 12.0156L10.7734 3.03906L12.375 4.64844L3.39844 13.6172ZM4.34375 14.3594L6.09375 12.6172H16.1328C16.6094 12.6172 17 13.0078 17 13.4844C17 13.9609 16.6094 14.3594 16.1328 14.3594H4.34375Z",
          fill: "#C2C2C2",
          style: { mixBlendMode: "overlay" },
        },
      ],
    }
  } else {
    // Plus icon for unconnected plugins (both instant and regular)
    return {
      viewBox: "0 0 15 16",
      fill: "none",
      className: "text-gray-400",
      fillPaths: [
        {
          d: "M13.8672 1.32812C14.6719 2.125 14.8438 3.22656 14.8438 4.71875V10.6562C14.8438 12.1484 14.6719 13.2422 13.8672 14.0469C13.0547 14.8516 11.9453 15.0312 10.4609 15.0312H4.53125C3.03906 15.0312 1.9375 14.8516 1.125 14.0469C0.320312 13.2422 0.148438 12.1484 0.148438 10.6562V4.70312C0.148438 3.22656 0.320312 2.13281 1.125 1.32812C1.92969 0.515625 3.04688 0.335938 4.51562 0.335938H10.4609C11.9453 0.335938 13.0547 0.515625 13.8672 1.32812ZM3.66406 7.6875C3.66406 8.17188 4.01562 8.51562 4.49219 8.51562H6.65625V10.6797C6.65625 11.1562 7 11.5078 7.48438 11.5078C7.96875 11.5078 8.32031 11.1562 8.32031 10.6797V8.51562H10.4922C10.9688 8.51562 11.3125 8.17188 11.3125 7.6875C11.3125 7.20312 10.9688 6.85156 10.4922 6.85156H8.32031V4.6875C8.32031 4.20312 7.96875 3.85938 7.48438 3.85938C7 3.85938 6.65625 4.20312 6.65625 4.6875V6.85156H4.49219C4.00781 6.85156 3.66406 7.20312 3.66406 7.6875Z",
          fill: "#7F7F7F",
          fillOpacity: 0.5,
          style: { mixBlendMode: "luminosity" },
        },
        {
          d: "M13.8672 1.32812C14.6719 2.125 14.8438 3.22656 14.8438 4.71875V10.6562C14.8438 12.1484 14.6719 13.2422 13.8672 14.0469C13.0547 14.8516 11.9453 15.0312 10.4609 15.0312H4.53125C3.03906 15.0312 1.9375 14.8516 1.125 14.0469C0.320312 13.2422 0.148438 12.1484 0.148438 10.6562V4.70312C0.148438 3.22656 0.320312 2.13281 1.125 1.32812C1.92969 0.515625 3.04688 0.335938 4.51562 0.335938H10.4609C11.9453 0.335938 13.0547 0.515625 13.8672 1.32812ZM3.66406 7.6875C3.66406 8.17188 4.01562 8.51562 4.49219 8.51562H6.65625V10.6797C6.65625 11.1562 7 11.5078 7.48438 11.5078C7.96875 11.5078 8.32031 11.1562 8.32031 10.6797V8.51562H10.4922C10.9688 8.51562 11.3125 8.17188 11.3125 7.6875C11.3125 7.20312 10.9688 6.85156 10.4922 6.85156H8.32031V4.6875C8.32031 4.20312 7.96875 3.85938 7.48438 3.85938C7 3.85938 6.65625 4.20312 6.65625 4.6875V6.85156H4.49219C4.00781 6.85156 3.66406 7.20312 3.66406 7.6875Z",
          fill: "#C2C2C2",
          style: { mixBlendMode: "overlay" },
        },
      ],
    }
  }
}

/**
 * Get icon props for a main plugin icon
 */
export const getMainPluginIconProps = (isInstant: boolean = false) => {
  const iconType = getMainPluginIconType(isInstant)

  switch (iconType) {
    case "lightning":
      return {
        viewBox: "0 0 24 24",
        fill: "none",
        className: "text-white",
        paths: ["M13 2L3 14h9l-1 8 10-12h-9l1-8z"],
      }
    case "puzzle":
      return {
        viewBox: "0 0 20 16",
        fill: "none",
        className: "text-white/70",
        paths: [
          "M0.335938 13.0234V9.72656C0.335938 9.14062 0.773438 8.78906 1.28125 8.78906C1.57812 8.78906 1.89844 8.92188 2.14844 9.19531C2.47656 9.53125 2.96875 10.0391 3.90625 10.0391C5.08594 10.0391 6.04688 9.09375 6.03125 7.89844C6.02344 6.70312 5.08594 5.75 3.90625 5.75C2.96875 5.75 2.47656 6.25781 2.14844 6.60156C1.89844 6.86719 1.57812 7 1.28125 7C0.773438 7 0.335938 6.65625 0.335938 6.0625V2.67969C0.335938 1 1.21875 0.117188 2.89062 0.117188H12.4844C14.1719 0.117188 15.0625 0.992188 15.0625 2.65625V5.70312C15.0625 5.875 15.1953 5.92188 15.3359 5.82031C15.7266 5.55469 16.1875 5.35156 16.7578 5.35156C18.1953 5.35156 19.2656 6.42188 19.2656 7.875C19.2656 9.32031 18.1953 10.3906 16.7578 10.3906C16.1875 10.3906 15.7266 10.1875 15.3359 9.92188C15.1953 9.82031 15.0625 9.875 15.0625 10.0391V13.0469C15.0625 14.7109 14.1719 15.5859 12.4844 15.5859H2.89062C1.21875 15.5859 0.335938 14.7031 0.335938 13.0234Z",
          "M0.335938 13.0234V9.72656C0.335938 9.14062 0.773438 8.78906 1.28125 8.78906C1.57812 8.78906 1.89844 8.92188 2.14844 9.19531C2.47656 9.53125 2.96875 10.0391 3.90625 10.0391C5.08594 10.0391 6.04688 9.09375 6.03125 7.89844C6.02344 6.70312 5.08594 5.75 3.90625 5.75C2.96875 5.75 2.47656 6.25781 2.14844 6.60156C1.89844 6.86719 1.57812 7 1.28125 7C0.773438 7 0.335938 6.65625 0.335938 6.0625V2.67969C0.335938 1 1.21875 0.117188 2.89062 0.117188H12.4844C14.1719 0.117188 15.0625 0.992188 15.0625 2.65625V5.70312C15.0625 5.875 15.1953 5.92188 15.3359 5.82031C15.7266 5.55469 16.1875 5.35156 16.7578 5.35156C18.1953 5.35156 19.2656 6.42188 19.2656 7.875C19.2656 9.32031 18.1953 10.3906 16.7578 10.3906C16.1875 10.3906 15.7266 10.1875 15.3359 9.92188C15.1953 9.82031 15.0625 9.875 15.0625 10.0391V13.0469C15.0625 14.7109 14.1719 15.5859 12.4844 15.5859H2.89062C1.21875 15.5859 0.335938 14.7031 0.335938 13.0234Z",
        ],
        fillPaths: [
          {
            d: "M0.335938 13.0234V9.72656C0.335938 9.14062 0.773438 8.78906 1.28125 8.78906C1.57812 8.78906 1.89844 8.92188 2.14844 9.19531C2.47656 9.53125 2.96875 10.0391 3.90625 10.0391C5.08594 10.0391 6.04688 9.09375 6.03125 7.89844C6.02344 6.70312 5.08594 5.75 3.90625 5.75C2.96875 5.75 2.47656 6.25781 2.14844 6.60156C1.89844 6.86719 1.57812 7 1.28125 7C0.773438 7 0.335938 6.65625 0.335938 6.0625V2.67969C0.335938 1 1.21875 0.117188 2.89062 0.117188H12.4844C14.1719 0.117188 15.0625 0.992188 15.0625 2.65625V5.70312C15.0625 5.875 15.1953 5.92188 15.3359 5.82031C15.7266 5.55469 16.1875 5.35156 16.7578 5.35156C18.1953 5.35156 19.2656 6.42188 19.2656 7.875C19.2656 9.32031 18.1953 10.3906 16.7578 10.3906C16.1875 10.3906 15.7266 10.1875 15.3359 9.92188C15.1953 9.82031 15.0625 9.875 15.0625 10.0391V13.0469C15.0625 14.7109 14.1719 15.5859 12.4844 15.5859H2.89062C1.21875 15.5859 0.335938 14.7031 0.335938 13.0234Z",
            fill: "#7F7F7F",
            fillOpacity: 0.5,
            style: { mixBlendMode: "luminosity" },
          },
          {
            d: "M0.335938 13.0234V9.72656C0.335938 9.14062 0.773438 8.78906 1.28125 8.78906C1.57812 8.78906 1.89844 8.92188 2.14844 9.19531C2.47656 9.53125 2.96875 10.0391 3.90625 10.0391C5.08594 10.0391 6.04688 9.09375 6.03125 7.89844C6.02344 6.70312 5.08594 5.75 3.90625 5.75C2.96875 5.75 2.47656 6.25781 2.14844 6.60156C1.89844 6.86719 1.57812 7 1.28125 7C0.773438 7 0.335938 6.65625 0.335938 6.0625V2.67969C0.335938 1 1.21875 0.117188 2.89062 0.117188H12.4844C14.1719 0.117188 15.0625 0.992188 15.0625 2.65625V5.70312C15.0625 5.875 15.1953 5.92188 15.3359 5.82031C15.7266 5.55469 16.1875 5.35156 16.7578 5.35156C18.1953 5.35156 19.2656 6.42188 19.2656 7.875C19.2656 9.32031 18.1953 10.3906 16.7578 10.3906C16.1875 10.3906 15.7266 10.1875 15.3359 9.92188C15.1953 9.82031 15.0625 9.875 15.0625 10.0391V13.0469C15.0625 14.7109 14.1719 15.5859 12.4844 15.5859H2.89062C1.21875 15.5859 0.335938 14.7031 0.335938 13.0234Z",
            fill: "#C2C2C2",
            style: { mixBlendMode: "overlay" },
          },
        ],
      }
    default:
      return {
        viewBox: "0 0 24 24",
        fill: "none",
        className: "text-gray-400",
        paths: ["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"],
      }
  }
}

export const renderIconByName = (iconName: string) => {
  switch (iconName) {
    case "links":
      return <IoLink className="h-5 w-5 text-white/50" />

    case "photos":
      return <MdPhotoLibrary className="h-5 w-5 text-white/50" />

    default:
      return <BsLightningCharge className="h-5 w-5 text-white/50" />
  }
}
